import type { QueryFunction, QueryKey } from '@tanstack/react-query'

import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { WilkerSwadayaDataType } from '../swadaya/types'
import type {
  ApbnApbdDataType,
  BidangKeahlianApbnApbdDataType,
  GetLogWilayahKerjaResponse,
  LogWilayahKerjaItem,
  PelatihanApbnApbdDataType,
  PendidikanApbnApbdDataType
} from './types'
import type {
  FilterApbnApbdValues,
  FilterSubitemApbnApbdValues,
  MutationApbnApbdValues,
  MutationBidangKeahlianApbnApbdValues,
  MutationPelatihanApbnApbdValues,
  MutationPendidikanApbnApbdValues,
  MutationPindahWilayahKerjaValues
} from './validation'

interface InfiniteQueryOptions<T> {
  queryKey: QueryKey
  initialPageParam: number
  getNextPageParam: (lastPage: ServiceGetAllResponse<T>) => number | undefined
  queryFn: QueryFunction<ServiceGetAllResponse<T>, QueryKey, number>
}

const APBN_APBD_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['apbn-apbd', key] as const

const getAll = (
  params?: FilterApbnApbdValues
): ServiceQueryOptionType<ServiceGetAllResponse<Array<ApbnApbdDataType>>> => ({
  queryKey: APBN_APBD_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<ApbnApbdDataType>>
      >(`/ketenagaan/penyuluh/thl_${params?.identifier}`, {
        params: {
          ...params,
          identifier: undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllApbnApbdInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<ApbnApbdDataType[]> => ({
  queryKey: APBN_APBD_QUERY_KEY({ ...params, type: 'thl-infinite' }),
  initialPageParam: 1,
  getNextPageParam: (lastPage: ServiceGetAllResponse<ApbnApbdDataType[]>) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<ServiceGetAllResponse<ApbnApbdDataType[]>>(
        `/ketenagaan/penyuluh/thl_${params?.identifier}`,
        {
          params: {
            ...params,
            page: pageParam,
            identifier: undefined
          }
        }
      )

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  identifier: string,
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<ApbnApbdDataType>> => ({
  queryKey: APBN_APBD_QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetByIdResponse<ApbnApbdDataType>>(
        `/ketenagaan/penyuluh/thl_${identifier}/${id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<ApbnApbdDataType>,
  MutationApbnApbdValues
> => ({
  mutationFn: async (data: MutationApbnApbdValues) => {
    try {
      let wilker: string[] = []

      if (data.jenis_wilker === 'KECAMATAN') {
        wilker =
          data.base_kecamatan?.flatMap(kecamatan =>
            kecamatan.id_desa.map(desa => desa.value)
          ) || []
      } else if (data.jenis_wilker === 'KOTA') {
        wilker = data.kecamatan_ids?.map(kecamatan => kecamatan.value) || []
      } else if (data.jenis_wilker === 'PROVINSI') {
        wilker = data.wilker_kota?.map(kota => kota.value) || []
      }

      const response = await api.post<ServiceGetByIdResponse<ApbnApbdDataType>>(
        `/ketenagaan/penyuluh/thl_${data?.identifier}`,
        {
          ...data,
          id_desa: data.id_desa.value,
          base_kecamatan: undefined,
          wilker_provinsi: undefined,
          id_status_pernikahan: data.id_status_pernikahan.value,
          id_agama: data.id_agama.value,
          jenjang_pendidikan: data.jenjang_pendidikan?.value,
          bpp: data.bpp?.value,
          kecamatan:
            data.jenis_wilker === 'KECAMATAN'
              ? data.base_kecamatan?.map(kecamatan => ({
                id: kecamatan.id_kecamatan.value,
                desa: kecamatan.id_desa.map(desa => desa.value)
              }))
              : data.kecamatan_ids?.map(kecamatan => ({
                id: kecamatan.value,
                desa: undefined
              })),
          wilker,
          id_subsektor: data.subsektor.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<ApbnApbdDataType>,
  MutationApbnApbdValues
> => ({
  mutationFn: async (data: MutationApbnApbdValues) => {
    try {
      let wilker: string[] = []

      if (data.jenis_wilker === 'KECAMATAN') {
        wilker =
          data.base_kecamatan?.flatMap(kecamatan =>
            kecamatan.id_desa.map(desa => desa.value)
          ) || []
      } else if (data.jenis_wilker === 'KOTA') {
        wilker = data.kecamatan_ids?.map(kecamatan => kecamatan.value) || []
      } else if (data.jenis_wilker === 'PROVINSI') {
        wilker = data.wilker_kota?.map(kota => kota.value) || []
      }

      const response = await api.put<ServiceGetByIdResponse<ApbnApbdDataType>>(
        `/ketenagaan/penyuluh/thl_${data?.identifier}/${data.id_apbn_apbd}`,
        {
          ...data,
          id_desa: data.id_desa.value,
          base_kecamatan: undefined,
          wilker_provinsi: undefined,
          id_status_pernikahan: data.id_status_pernikahan.value,
          id_agama: data.id_agama.value,
          jenjang_pendidikan: data.jenjang_pendidikan?.value,
          bpp: data.bpp?.value,
          kecamatan:
            data.jenis_wilker === 'KECAMATAN'
              ? data.base_kecamatan?.map(kecamatan => ({
                id: kecamatan.id_kecamatan.value,
                desa: kecamatan.id_desa.map(desa => desa.value)
              }))
              : data.kecamatan_ids?.map(kecamatan => ({
                id: kecamatan.value,
                desa: undefined
              })),
          wilker,
          id_subsektor: data.subsektor.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<ApbnApbdDataType>,
  { id_poktan: string; identifier: string }
> => ({
  mutationFn: async (data: { id_poktan: string; identifier: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<ApbnApbdDataType>
      >(`/ketenagaan/penyuluh/thl_${data?.identifier}/${data.id_poktan}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const exportData = (params: { identifier: string; filter?: FilterApbnApbdValues }) => ({
  mutationFn: async () => {
    try {
      const response = await api.get<{
        status: string
        url?: string | null
        job_id?: string
        message?: string
        progress?: number
      }>(`/ketenagaan/penyuluh/${params?.identifier}/export`, {
        params: {
          ...params?.filter,
          jenis: params?.identifier
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

//START Pelatihan
const getAllPelatihan = (
  params?: FilterSubitemApbnApbdValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PelatihanApbnApbdDataType>>
> => ({
  queryKey: APBN_APBD_QUERY_KEY({ ...params, type: 'pelatihan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PelatihanApbnApbdDataType>>
      >(
        `/ketenagaan/penyuluh/thl_${params?.identifier}/${params?.id_apbn_apbd}/pelatihan`,
        {
          params: {
            ...params,
            id_apbn_apbd: undefined,
            identifier: undefined
          }
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createPelatihan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PelatihanApbnApbdDataType>,
  MutationPelatihanApbnApbdValues
> => ({
  mutationFn: async (data: MutationPelatihanApbnApbdValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<PelatihanApbnApbdDataType>
      >(
        `/ketenagaan/penyuluh/thl_${data?.identifier}/${data?.id_apbn_apbd}/pelatihan`,
        {
          ...data
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updatePelatihan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PelatihanApbnApbdDataType>,
  MutationPelatihanApbnApbdValues
> => ({
  mutationFn: async (data: MutationPelatihanApbnApbdValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PelatihanApbnApbdDataType>
      >(
        `/ketenagaan/penyuluh/thl_${data?.identifier}/${data?.id_apbn_apbd}/pelatihan/${data.id_pelatihan}`,
        {
          ...data
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deletePelatihan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PelatihanApbnApbdDataType>,
  { id_apbn_apbd: string; id_data: string; identifier: string }
> => ({
  mutationFn: async (data: {
    id_apbn_apbd: string
    id_data: string
    identifier: string
  }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PelatihanApbnApbdDataType>
      >(
        `/ketenagaan/penyuluh/thl_${data?.identifier}/${data?.id_apbn_apbd}/pelatihan/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

//START BIDANG KEAHLIAN
const getAllBidangKeahlian = (
  params?: FilterSubitemApbnApbdValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<BidangKeahlianApbnApbdDataType>>
> => ({
  queryKey: APBN_APBD_QUERY_KEY({ ...params, type: 'bidang-keahlian' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<BidangKeahlianApbnApbdDataType>>
      >(
        `/ketenagaan/penyuluh/thl_${params?.identifier}/${params?.id_apbn_apbd}/keahlian`,
        {
          params: {
            ...params,
            id_apbn_apbd: undefined,
            identifier: undefined
          }
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createBidangKeahlian = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<BidangKeahlianApbnApbdDataType>,
  MutationBidangKeahlianApbnApbdValues
> => ({
  mutationFn: async (data: MutationBidangKeahlianApbnApbdValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<BidangKeahlianApbnApbdDataType>
      >(
        `/ketenagaan/penyuluh/thl_${data?.identifier}/${data?.id_apbn_apbd}/keahlian`,
        {
          ...data,
          id_subsektor: data.subsektor.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updateBidangKeahlian = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<BidangKeahlianApbnApbdDataType>,
  MutationBidangKeahlianApbnApbdValues
> => ({
  mutationFn: async (data: MutationBidangKeahlianApbnApbdValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<BidangKeahlianApbnApbdDataType>
      >(
        `/ketenagaan/penyuluh/thl_${data?.identifier}/${data?.id_apbn_apbd}/keahlian/${data.id_bidang_keahlian}`,
        {
          ...data,
          id_subsektor: data.subsektor.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteBidangKeahlian = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<BidangKeahlianApbnApbdDataType>,
  { id_apbn_apbd: string; id_data: string; identifier: string }
> => ({
  mutationFn: async (data: {
    id_apbn_apbd: string
    id_data: string
    identifier: string
  }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<BidangKeahlianApbnApbdDataType>
      >(
        `/ketenagaan/penyuluh/thl_${data?.identifier}/${data?.id_apbn_apbd}/keahlian/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

//START PENDIDIKAN
const getAllPendidikan = (
  params?: FilterSubitemApbnApbdValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PendidikanApbnApbdDataType>>
> => ({
  queryKey: APBN_APBD_QUERY_KEY({ ...params, type: 'pendidikan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PendidikanApbnApbdDataType>>
      >(
        `/ketenagaan/penyuluh/thl_${params?.identifier}/${params?.id_apbn_apbd}/pendidikan`,
        {
          params: {
            ...params,
            id_apbn_apbd: undefined,
            identifier: undefined
          }
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createPendidikan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PendidikanApbnApbdDataType>,
  MutationPendidikanApbnApbdValues
> => ({
  mutationFn: async (data: MutationPendidikanApbnApbdValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<PendidikanApbnApbdDataType>
      >(
        `/ketenagaan/penyuluh/thl_${data?.identifier}/${data?.id_apbn_apbd}/pendidikan`,
        {
          ...data,
          jenjang_pendidikan: data.jenjang_pendidikan.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updatePendidikan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PendidikanApbnApbdDataType>,
  MutationPendidikanApbnApbdValues
> => ({
  mutationFn: async (data: MutationPendidikanApbnApbdValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PendidikanApbnApbdDataType>
      >(
        `/ketenagaan/penyuluh/thl_${data?.identifier}/${data?.id_apbn_apbd}/pendidikan/${data.id_pendidikan}`,
        {
          ...data,
          jenjang_pendidikan: data.jenjang_pendidikan.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deletePendidikan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PendidikanApbnApbdDataType>,
  { id_apbn_apbd: string; id_data: string; identifier: string }
> => ({
  mutationFn: async (data: {
    id_apbn_apbd: string
    id_data: string
    identifier: string
  }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PendidikanApbnApbdDataType>
      >(
        `/ketenagaan/penyuluh/thl_${data?.identifier}/${data?.id_apbn_apbd}/pendidikan/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllWilayahKerja = (
  params?: FilterSubitemApbnApbdValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<WilkerSwadayaDataType>>
> => ({
  queryKey: APBN_APBD_QUERY_KEY({ ...params, type: 'wilayah-kerja' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<WilkerSwadayaDataType>>
      >(
        `/ketenagaan/penyuluh/thl_${params?.identifier}/${params?.id_apbn_apbd}/wilayah-kerja`,
        {
          params
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createPindahWilayahKerja = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<LogWilayahKerjaItem>,
  { id_penyuluh: string; data: MutationPindahWilayahKerjaValues }
> => ({
  mutationFn: async (payload: {
    id_penyuluh: string
    data: MutationPindahWilayahKerjaValues
  }) => {
    try {
      const formData = new FormData()
      formData.append('id_jenis_pp', payload.data.id_jenis_pp)
      formData.append('jenis_wilker_lama', payload.data.jenis_wilker_lama)
      formData.append('jenis_wilker_baru', payload.data.jenis_wilker_baru)
      formData.append('alasan', payload.data.alasan ?? '')
      formData.append('tanggal', payload.data.tanggal)
      if (payload.data.lampiran) {
        formData.append('lampiran', payload.data.lampiran)
      }

      // Append wilker_lama array
      if (payload.data.wilker_lama && payload.data.wilker_lama.length > 0) {
        payload.data.wilker_lama.forEach(id => {
          formData.append('wilker_lama[]', id)
        })
      }

      // Append wilker_baru array
      if (payload.data.wilker_baru && payload.data.wilker_baru.length > 0) {
        payload.data.wilker_baru.forEach(id => {
          formData.append('wilker_baru[]', id)
        })
      }

      if (payload.data.penempatan?.jenis && payload.data.penempatan?.id) {
        formData.append('penempatan[jenis]', payload.data.penempatan.jenis)
        formData.append('penempatan[id]', payload.data.penempatan.id)
      }

      const response = await api.post<
        ServiceGetByIdResponse<LogWilayahKerjaItem>
      >(`/ketenagaan/penyuluh/${payload.id_penyuluh}/log-wilker`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getLogWilayahKerja = (
  id: string,
  filters?: Partial<{ page: number; per_page: number }>
): ServiceQueryOptionType<
  QueryFunction<GetLogWilayahKerjaResponse, QueryKey>,
  GetLogWilayahKerjaResponse
> => ({
  queryKey: ['apbn-apbd-log-wilayah-kerja', id, filters],
  queryFn: async () => {
    try {
      const response = await api.get<GetLogWilayahKerjaResponse>(
        `/ketenagaan/penyuluh/${id}/log-wilker`,
        { params: filters }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  },
  enabled: !!id
})

export const apbnApbd = {
  queryKey: [APBN_APBD_QUERY_KEY(undefined)[0]],
  getAll,
  getAllApbnApbdInfinite,
  getById,
  create,
  update,
  deleteData,
  exportData,
  getAllPelatihan,
  createPelatihan,
  updatePelatihan,
  deletePelatihan,

  getAllBidangKeahlian,
  createBidangKeahlian,
  updateBidangKeahlian,
  deleteBidangKeahlian,

  getAllPendidikan,
  createPendidikan,
  updatePendidikan,
  deletePendidikan,

  getAllWilayahKerja,
  createPindahWilayahKerja,
  getLogWilayahKerja
}
